import angular from "angular";
import { SummaryReportData, Numeric, Translations, Language, Configs, Ajax, WorkerSummaryReportData } from "@salaxy/core";
import { SettingsService, UiHelpers, SessionService } from "../../services";
import { WorkerSummaryMapper, ReportData, ReportOptions, Templates } from "@salaxy/reports";


/**
 * Renders a worker summary report or a calculation summary report for the given calculation(s).
 * Uses Handlebars templates (@salaxy/reports).
 */
export class WorkerSummaryReportController implements angular.IController {
  public data: WorkerSummaryReportData;
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Templates", "$sce", "SettingsService", "$timeout", "$http", "UiHelpers", "SessionService", "AjaxNg1"];

  /** Template for the report */
  public templateName = "workerSummaryReport";

  /** Boolean indicating if the refresh is in progress. */
  public refreshInprogress: boolean;

  /**Selected report language */
  public lang: Language;

  private renderedData: WorkerSummaryReportData;

  private renderedLang: string;

  private htmlData: any;

  private reportOptions: ReportOptions;

  private reportTemplate = "workerSummaryReport";

  private reportData: ReportData<WorkerSummaryReportData> = {
    headerFooter: {},
    layout: {},
    report: {},
  };

  private _reportLang: Language = Language.Default;



  /**
   * Creates a new CalcReportController
   * @ignore
   */
  constructor(private templates: Templates,
    private $sce: angular.ISCEService,
    private settingsService: SettingsService,
    private $timeout: angular.ITimeoutService,
    private $http: angular.IHttpService,
    private uiHelpers: UiHelpers,
    private sessionService: SessionService,
    private ajax: Ajax
  ) { }

  /**
   * Implement IController
   */
  public $onInit() {
    this.sessionService.getReportLang().then((lang) => {
      this.reportLang = lang;
    });
  }

  /**
   * Get _reportLang
   */
  public get reportLang() {
    return this._reportLang;
  }

  /**
   * Set _reportLang & lang
   */
  public set reportLang(lang: Language) {
    this._reportLang = lang;
    if (lang !== Language.Default) {
      this.lang = this._reportLang;
    } else {
      this.sessionService.getReportLang().then(lang => {
        this.lang = lang;
      });
    }
  }

  /**
   * Return languge text to ui
   */
  public get languageText() {
    switch (this.reportLang) {
      case Language.Fi:
        return "SALAXY.ENUM.Language.fi.label";
      case Language.En:
        return "SALAXY.ENUM.Language.en.label";
      case Language.Sv:
        return "SALAXY.ENUM.Language.sv.label";
      case Language.Default:
        return "SALAXY.NG1.CalcReportsComponent.reportLanguageCompany";
    }
  }

  /** Bindable and trusted html. */
  public get html(): any {
    if (!this.data) {
      this.renderedData = null;
      this.renderedLang = null;
    } else {
      const workerSummaryReportData = angular.copy(this.data);

      if (!this.renderedData || !angular.equals(workerSummaryReportData, this.renderedData)
        || this.renderedLang !== this.lang) {
        this.lang = this.lang === Language.Default ? Language.Fi : this.lang;
        this.renderedData = angular.copy(workerSummaryReportData);
        this.renderedLang = this.lang;
        this.refresh();
      }
    }
    return this.htmlData;
  }

  private async refresh() {
    if (this.refreshInprogress) {
      return;
    }
    this.refreshInprogress = true;
    this.settingsService.getReportOptions().then(async (options) => {
      this.reportOptions = options;
      const renderingLang = this.lang ?? Language.Fi;
      await Translations.loadLanguage(renderingLang);
      const reportOptions = this.getDefaultReportOptions(angular.copy(this.reportOptions));
      this.reportData.headerFooter = WorkerSummaryMapper.getWorkerSummaryReportHeaderFooter(this.data, reportOptions, renderingLang);
      this.reportData.report = WorkerSummaryMapper.getWorkerSummaryReport(this.data, renderingLang);
      this.reportData.layout = WorkerSummaryMapper.getReportLayout(true, false);
      const css = "";
      this.reportData.layout.hasCustomCss = false;
      this.reportData.layout.customCss = (css || "");
      this.reportData.layout.margin = {
        top: reportOptions.margin.top,
        right: reportOptions.margin.right,
        bottom: reportOptions.margin.bottom,
        left: reportOptions.margin.left
      };
      const templates = new Templates();
      this.reportData.headerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.headerTemplate, this.reportData.headerFooter, renderingLang) : "";
      this.reportData.footerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.footerTemplate, this.reportData.headerFooter, renderingLang) : "";
      this.htmlData = this.$sce.trustAsHtml(this.templates.getHtml(this.templateName, this.reportData, renderingLang));
      this.refreshInprogress = false;
      this.$timeout();
    });
  }
  /**
   * Shows a download dialog for the given summary report.
   * @param inline - If true, the download link opens the pdf into a new web page.
   */
  public showDownloadDialog(inline = false) {
    const url = `${Configs.current.reportServer}/pdf?id=&reportType=${encodeURIComponent("yearlyWorkerSummary")}&template=${encodeURIComponent(this.reportTemplate)}&language=${encodeURIComponent(this.lang)}&disposition=inline&access_token=${this.ajax.getCurrentToken()}`;
    this.handlePdfRequest(url, inline, this.data);
  }

  private getDefaultReportOptions(optionsInput?: ReportOptions): ReportOptions {
    optionsInput = optionsInput || {};
    optionsInput.margin = optionsInput.margin || {};
    optionsInput.margin.top = Numeric.parseNumber(optionsInput.margin.top) || 30;
    optionsInput.margin.right = Numeric.parseNumber(optionsInput.margin.right) || 10;
    optionsInput.margin.bottom = Numeric.parseNumber(optionsInput.margin.bottom) || 40;
    optionsInput.margin.left = Numeric.parseNumber(optionsInput.margin.left) || 10;
    return optionsInput;
  }

  /**
   * Handles pdf request
   * @param url post url
   * @param inline inline pdf
   * @param data business object
   */
  private handlePdfRequest(url: string, inline: boolean, data: SummaryReportData): void {
    this.$http.post(url, { object: data }, { responseType: "blob" }).then((response) => {
      if (response.data) {
        if (inline) {
          this.uiHelpers.openFile(response.data as any);
        } else {
          this.uiHelpers.downloadFile(response.data as any, response.headers("content-disposition").split(";")[1].split("filename")[1].split("=")[1].trim().replace(/"/g, ""));
        }
      }
    }, (error) => {
      console.error(error);
    });
  }

}

